import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SignInForm } from './SignInForm';
import { SignUpForm } from './SignUpForm';
import { sectionNumber } from '@/lib/utils';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which tab to start on. Defaults to 'signin'. */
  initialTab?: 'signin' | 'signup';
  /** Called after a successful sign-in or sign-up, after the modal closes. */
  onAuthenticated?: () => void;
}

/**
 * Real auth modal. Two tabs, controlled internally. On a successful
 * sign-in or sign-up, the modal closes itself.
 */
export function AuthModal({
  open,
  onOpenChange,
  initialTab = 'signin',
  onAuthenticated,
}: AuthModalProps) {
  const [tab, setTab] = useState<'signin' | 'signup'>(initialTab);

  // Reset to the requested initial tab whenever the modal re-opens.
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  const onSuccess = () => {
    onOpenChange(false);
    onAuthenticated?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <p className="marginalia">
            <span className="mr-2">{sectionNumber(0)}</span>access
          </p>
          <DialogTitle className="italic">
            {tab === 'signin' ? 'Welcome back.' : 'Create your account.'}
          </DialogTitle>
          <DialogDescription>
            {tab === 'signin'
              ? 'Sign in to continue your projects. AURA keeps one active session per account — signing in here signs out any other device.'
              : 'No verification email, no password reset in this build. Use a password you will remember.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'signin' | 'signup')}>
          <TabsList>
            <TabsTrigger value="signin">Sign in</TabsTrigger>
            <TabsTrigger value="signup">Sign up</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <SignInForm onSuccess={onSuccess} />
          </TabsContent>

          <TabsContent value="signup">
            <SignUpForm onSuccess={onSuccess} onSwitchToSignIn={() => setTab('signin')} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
